import { useState } from "react";

/**
 * Reads a CSS custom property (stored as an HSL triplet, e.g. "210 100%
 * 40%") and wraps it as a literal `hsl(...)` colour string. Components that
 * take a literal colour rather than a Tailwind class (e.g.
 * LiveAudioVisualizer's barColor) can't reference a theme token directly, so
 * this resolves one at runtime instead of hardcoding a hex that would drift
 * from the token and never follow a custom theme or dark mode.
 */
const readThemeColor = (cssVar: string, fallback: string) => {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(cssVar)
    .trim();
  return raw ? `hsl(${raw})` : fallback;
};

/**
 * Resolves a theme token to a literal CSS colour once, at mount — see
 * `readThemeColor`. `cssVar`/`fallback` are read once via the lazy
 * initializer; a live theme switch mid-session isn't a use case this needs
 * to react to.
 */
export const useThemeColor = (cssVar: string, fallback: string): string => {
  const [color] = useState(() => readThemeColor(cssVar, fallback));
  return color;
};
